import React, { useContext, useState, useEffect, useRef } from "react";
import { TranslatorContext } from "../../context/Translator";
import { ButtonComponent, AnchorComponent } from "../../components/elements";
import IconFieldComponent from "../../components/fields/IconFieldComponent";
import LogoComponent from "../../components/LogoComponent";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import { useValidation } from "../../context/ValidationContext";
import { saveSession, clearSession } from "../../utils/session";

const CODE_LENGTH = 6;

export default function LoginPage() {
  const { login } = useAuth();
  const { validateEmail } = useValidation();
  const { t } = useContext(TranslatorContext);

  const validatePassword = (password) => password.length >= 8;

  const [values, setValues] = useState({ email: "", password: "" });
  const [errors, setErrors] = useState({ email: "", password: "" });
  const [submitted, setSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [step, setStep] = useState("credentials");
  const [verification, setVerification] = useState(null);
  const [digits, setDigits] = useState(Array(CODE_LENGTH).fill(""));
  const [codeError, setCodeError] = useState("");
  const [notice, setNotice] = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [resending, setResending] = useState(false);

  const inputRefs = useRef([]);

  useEffect(() => {
    const reason = new URLSearchParams(window.location.search).get("reason");

    if (reason === "expired") {
      clearSession();
      setNotice({ tone: "is-warning", text: "Your session has expired. Please sign in again." });
    } else if (reason === "inactive") {
      clearSession();
      setNotice({
        tone: "is-warning",
        text: "Your account has been deactivated. Please contact the administrator.",
      });
    }
  }, []);

  useEffect(() => {
    if (step !== "verify") return undefined;

    inputRefs.current[0]?.focus();
    return undefined;
  }, [step]);

  useEffect(() => {
    if (step !== "verify") return undefined;
    if (secondsLeft <= 0) return undefined;

    const timer = setInterval(() => {
      setSecondsLeft((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);

    return () => clearInterval(timer);
  }, [step, secondsLeft]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setValues((prev) => ({ ...prev, [name]: value }));

    if (submitted) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const completeLogin = (data) => {
    if (!data?.token) {
      clearSession();
      setStep("credentials");
      setVerification(null);
      setNotice({
        tone: "is-warning",
        text: "Sign in could not be completed. Please sign in again.",
      });
      setIsLoading(false);
      return;
    }

    saveSession(data);
    login(data);

    window.location.href = "/dashboard";
  };

  const returnToLogin = (message) => {
    clearSession();
    setStep("credentials");
    setVerification(null);
    setDigits(Array(CODE_LENGTH).fill(""));
    setCodeError("");
    setNotice({ tone: "is-warning", text: message });
    setIsLoading(false);
  };

  const startVerification = (data) => {
    setVerification(data);
    setSecondsLeft(data?.expires_in || 600);
    setDigits(Array(CODE_LENGTH).fill(""));
    setCodeError("");
    setStep("verify");
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setSubmitted(true);
    setNotice(null);

    let newErrors = { email: "", password: "" };
    let hasError = false;

    if (!values.email.trim()) {
      newErrors.email = "Email is required";
      hasError = true;
    } else if (!validateEmail(values.email)) {
      newErrors.email = "Invalid email address";
      hasError = true;
    }

    if (!values.password.trim()) {
      newErrors.password = "Password is required";
      hasError = true;
    } else if (!validatePassword(values.password)) {
      newErrors.password = "Password must be at least 8 characters";
      hasError = true;
    }

    if (hasError) {
      setErrors(newErrors);
      return;
    }

    setIsLoading(true);

    try {
      const res = await axios.post(process.env.REACT_APP_API_URL + "/login", {
        email: values.email.trim(),
        password: values.password.trim(),
      });

      if (res?.data?.success && res.data.requires_verification) {
        clearSession();
        startVerification(res.data.data);
        setIsLoading(false);
        return;
      }

      if (res?.data?.success) {
        completeLogin(res.data.data);
        return;
      }

      setErrors({ ...newErrors, password: "Invalid email or password" });
      setIsLoading(false);
    } catch (error) {
      const data = error?.response?.data;

      if (
        data?.error_code === "ACCOUNT_INACTIVE" ||
        data?.error_code === "VERIFICATION_MAIL_FAILED"
      ) {
        setNotice({ tone: "is-warning", text: data.message });
      } else {
        setErrors({ ...newErrors, password: "Invalid email or password" });
      }

      setIsLoading(false);
    }
  };

  const submitCode = async (code) => {
    setCodeError("");
    setNotice(null);
    setIsLoading(true);

    try {
      const res = await axios.post(process.env.REACT_APP_API_URL + "/login/verify", {
        verification_token: verification?.verification_token,
        code,
      });

      if (res?.data?.success) {
        completeLogin(res.data.data);
        return;
      }

      returnToLogin(
        res?.data?.message || "The verification code could not be verified. Please sign in again."
      );
    } catch (error) {
      const data = error?.response?.data;

      returnToLogin(
        data?.message || "The verification code could not be verified. Please sign in again."
      );
    }
  };

  const handleVerify = (e) => {
    e.preventDefault();

    const code = digits.join("");

    if (code.length < CODE_LENGTH) {
      setCodeError(`Enter all ${CODE_LENGTH} digits of the code.`);
      return;
    }

    submitCode(code);
  };

  const handleDigitChange = (index, rawValue) => {
    const value = rawValue.replace(/\D/g, "");

    if (!value) {
      setDigits((prev) => {
        const next = [...prev];
        next[index] = "";
        return next;
      });
      return;
    }

    setDigits((prev) => {
      const next = [...prev];
      const chars = value.split("");

      for (let i = 0; i < chars.length && index + i < CODE_LENGTH; i += 1) {
        next[index + i] = chars[i];
      }

      const filled = next.filter(Boolean).length;
      const focusAt = Math.min(index + chars.length, CODE_LENGTH - 1);

      inputRefs.current[focusAt]?.focus();

      if (filled === CODE_LENGTH && !isLoading) {
        submitCode(next.join(""));
      }

      return next;
    });

    setCodeError("");
  };

  const handleDigitKeyDown = (index, e) => {
    if (e.key === "Backspace" && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }

    if (e.key === "ArrowLeft" && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }

    if (e.key === "ArrowRight" && index < CODE_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    const pasted = (e.clipboardData.getData("text") || "").replace(/\D/g, "");

    if (!pasted) return;

    e.preventDefault();
    handleDigitChange(0, pasted.slice(0, CODE_LENGTH));
  };

  const handleResend = async () => {
    setResending(true);
    setCodeError("");
    setNotice(null);

    try {
      const res = await axios.post(process.env.REACT_APP_API_URL + "/login/resend", {
        verification_token: verification?.verification_token,
      });

      if (res?.data?.success) {
        startVerification(res.data.data);
        setNotice({ tone: "is-info", text: res.data.message });
      }
    } catch (error) {
      const data = error?.response?.data;

      setNotice({
        tone: "is-warning",
        text: data?.message || "Could not resend the verification code.",
      });

      if (data?.error_code === "VERIFICATION_NOT_FOUND") {
        setStep("credentials");
        setVerification(null);
      }
    } finally {
      setResending(false);
    }
  };

  const backToCredentials = () => {
    setStep("credentials");
    setVerification(null);
    setDigits(Array(CODE_LENGTH).fill(""));
    setCodeError("");
    setNotice(null);
  };

  const formatCountdown = () => {
    const minutes = Math.floor(secondsLeft / 60);
    const seconds = secondsLeft % 60;

    return `${minutes}:${String(seconds).padStart(2, "0")}`;
  };

  return (
    <div className="mc-auth">
      <img src="/images/pattern.webp" alt="pattern" className="mc-auth-pattern" />

      <div className="mc-auth-group">
        <LogoComponent
          src="/images/top-logo04.png"
          alt="logo"
          href="/"
          className="mc-auth-logo"
        />

        {notice && (
          <div className={`mc-auth-notice ${notice.tone}`}>{notice.text}</div>
        )}

        {step === "credentials" ? (
          <>
            <h4 className="mc-auth-title">Login to JPH</h4>

            <form onSubmit={handleLogin} className="mc-auth-form">
              <IconFieldComponent
                icon="email"
                type="email"
                name="email"
                onChange={handleChange}
                classes="w-100 h-sm"
                placeholder="Enter your email"
                value={values.email}
              />

              {errors.email && (
                <span style={{ color: "red", fontSize: "14px" }}>{errors.email}</span>
              )}

              <IconFieldComponent
                icon="lock"
                type="password"
                name="password"
                onChange={handleChange}
                classes="w-100 h-sm"
                placeholder="Enter your password"
                passwordVisible={true}
                value={values.password}
              />

              {errors.password && (
                <span style={{ color: "red", fontSize: "14px" }}>{errors.password}</span>
              )}

              <ButtonComponent
                className="mc-auth-btn h-sm"
                type="submit"
                disabled={isLoading}
              >
                {isLoading ? "Loading..." : t("sign_in")}
              </ButtonComponent>

              <AnchorComponent className="mc-auth-forgot" to="/forgot-password">
                {t("forgot_password")}
              </AnchorComponent>
            </form>
          </>
        ) : (
          <div className="mc-auth-verify">
            <div className="mc-auth-verify-icon">
              <i className="material-icons">mark_email_read</i>
            </div>

            <h4 className="mc-auth-title">Verify your sign in</h4>

            <p className="mc-auth-verify-text">
              We sent a {CODE_LENGTH} digit code to
            </p>
            <p className="mc-auth-verify-text">
              <strong>{verification?.email}</strong>
            </p>

            <form onSubmit={handleVerify} className="mc-auth-form">
              <div className="mc-auth-otp" onPaste={handlePaste}>
                {digits.map((digit, index) => (
                  <input
                    key={index}
                    ref={(el) => {
                      inputRefs.current[index] = el;
                    }}
                    className={`mc-auth-otp-input ${digit ? "is-filled" : ""} ${
                      codeError ? "is-error" : ""
                    }`}
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={CODE_LENGTH}
                    value={digit}
                    disabled={isLoading}
                    onChange={(e) => handleDigitChange(index, e.target.value)}
                    onKeyDown={(e) => handleDigitKeyDown(index, e)}
                    onFocus={(e) => e.target.select()}
                  />
                ))}
              </div>

              {codeError && <span className="mc-auth-otp-error">{codeError}</span>}

              <div className="mc-auth-verify-meta">
                <span>
                  {secondsLeft > 0
                    ? `Expires in ${formatCountdown()}`
                    : "This code has expired"}
                </span>

                <button
                  type="button"
                  className="mc-auth-link-btn"
                  onClick={handleResend}
                  disabled={resending}
                >
                  {resending ? "Sending..." : "Resend code"}
                </button>
              </div>

              <ButtonComponent
                className="mc-auth-btn h-sm"
                type="submit"
                disabled={isLoading}
              >
                {isLoading ? "Verifying..." : "Verify and sign in"}
              </ButtonComponent>

              <button type="button" className="mc-auth-back-btn" onClick={backToCredentials}>
                Use a different account
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
